import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminUsers.css';
import { Circles } from 'react-loader-spinner';
import privateAxios from '../../utils/axios/privateAxios';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { villages, towns, educationLevels, occupations, rolesList, stateOfResidence } from '../../assets/exports.js';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog.jsx';

const AdminUsers = () => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useState({
    email: '',
    firstname: '',
    lastname: '',
    role: '',
    village: '',
    town: '',
    educationLevel: '',
    occupation: '',
    firstAge: '',
    secondAge: ''
  });

  const ageRange = Array.from({ length: 101 }, (_, i) => i);
  const [searchOptions, setSearchOptions] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchMode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // can be 'single' or 'multiple'
  const [deleteUserId, setDeleteUserId] = useState(null); // ID for single delete
  const [dialogLoading, setDialogLoading] = useState(false);


  const fetchSetUsers = async (pageNumber) => {
    try {
      setLoading(true);
      const response = await privateAxios.get(`/private/admin-users?page=${pageNumber}`);
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
      setCurrentPage(pageNumber);
    } catch (err) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSetSearchedUsers = async (pageNumber) => {
    try {
      setLoading(true);
      const query = new URLSearchParams({ ...searchParams, page: pageNumber });
      const response = await privateAxios.get(`/private/admin-searched-users?${query.toString()}`);
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
      setCurrentPage(pageNumber);
    } catch (err) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const { firstAge, secondAge } = searchParams;
    if (firstAge && secondAge && parseInt(firstAge) >= parseInt(secondAge)) {
      alert('First age must be less than second age');
      return;
    }

    setSearchMode(true);
    fetchSetSearchedUsers(1);
  };

  const handleConfirmDelete = async () => {
    setDialogLoading(true);
    try {
      if (deleteTarget === 'single' && deleteUserId) {
        await privateAxios.delete(`/private/delete-user/${deleteUserId}`);
      } else if (deleteTarget === 'multiple') {
        await privateAxios.post('/private/delete-users', { ids: selectedUsers });
        setSelectedUsers([]);
      }

      // Refresh users
      searchMode ? fetchSetSearchedUsers(currentPage) : fetchSetUsers(currentPage);
    } catch (err) {
      console.error('Delete failed', err);
    } finally {
      setDialogLoading(false);
      setShowConfirmDialog(false);
      setDeleteUserId(null);
      setDeleteTarget(null);
    }
  };


  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem('userRoles')) || [];
    if (!roles.includes('admin')) navigate('/unauthorized');
    else fetchSetUsers(1);
  }, [navigate]);

  const handleSearchChange = (e) => {
    setSearchParams({ ...searchParams, [e.target.name]: e.target.value });
  };

  const handleCheckbox = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id]
    );
  };

  const deleteSelectedUsers = async () => {
    if (!selectedUsers.length) return;
    try {
      await privateAxios.post('/private/delete-users', { ids: selectedUsers });
      setSelectedUsers([]);
      searchMode ? fetchSetSearchedUsers(currentPage) : fetchSetUsers(currentPage);
    } catch (error) {
      console.error('Delete failed', error);
    }
  };

  const handleUserClick = (id, e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    navigate(`/admin-user-profile/${id}`);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      searchMode ? fetchSetSearchedUsers(nextPage) : fetchSetUsers(nextPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      searchMode ? fetchSetSearchedUsers(prevPage) : fetchSetUsers(prevPage);
    }
  };

  return (
    <div className="admin-users">
      <div className="admin-users-header">
        <div className='admin-users-toggle-back-actions'>
          <button className="admin-users-back-btn" onClick={() => navigate('/admin-dashboard')}>
            Back to Admin Dashboard
          </button>
          <button className="admin-users-back-btn" onClick={() => { setSearchOptions(!searchOptions) }}>Show Search Options</button>
        </div>

        {
          searchOptions &&
          <div className="admin-users-search-fields">
            <input type="text" name="email" placeholder="Email" value={searchParams.email} onChange={handleSearchChange} />
            <input type="text" name="firstname" placeholder="First Name" value={searchParams.firstname} onChange={handleSearchChange} />
            <input type="text" name="lastname" placeholder="Last Name" value={searchParams.lastname} onChange={handleSearchChange} />

            <select name="role" value={searchParams.role} onChange={handleSearchChange}>
              <option value="">Select Role</option>
              {rolesList.map(role => <option key={role} value={role}>{role}</option>)}
            </select>

            <select name="village" value={searchParams.village} onChange={handleSearchChange}>
              <option value="">Select Village</option>
              {villages.map(v => <option key={v} value={v}>{v}</option>)}
            </select>

            <select name="town" value={searchParams.town} onChange={handleSearchChange}>
              <option value="">Select Town</option>
              {towns.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>

            <select name="stateOfResidence" value={searchParams.town} onChange={handleSearchChange}>
              <option value="">Select State of Residence</option>
              {stateOfResidence.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>

            <select name="educationLevel" value={searchParams.educationLevel} onChange={handleSearchChange}>
              <option value="">Education Level</option>
              {educationLevels.map(level => <option key={level} value={level}>{level}</option>)}
            </select>

            <select name="occupation" value={searchParams.occupation} onChange={handleSearchChange}>
              <option value="">Occupation</option>
              {occupations.map(occ => <option key={occ} value={occ}>{occ}</option>)}
            </select>

            <select name="firstAge" value={searchParams.firstAge} onChange={handleSearchChange}>
              <option value="">From Age</option>
              {ageRange.map(age => <option key={age} value={age}>{age}</option>)}
            </select>

            <select name="secondAge" value={searchParams.secondAge} onChange={handleSearchChange}>
              <option value="">To Age</option>
              {ageRange.map(age => <option key={age} value={age}>{age}</option>)}
            </select>

            <button className='admin-users-search-button' onClick={handleSearch}>Search</button>
          </div>
        }

        <button
          className="admin-users-delete-selected-btn"
          onClick={() => {
            setDeleteTarget('multiple');
            setShowConfirmDialog(true);
          }}
          disabled={!selectedUsers.length}
          style={{ backgroundColor: selectedUsers.length ? '#008751' : '#ccf1e4' }}
        >
          Delete Selected Users
        </button>
      </div>

      <div className="admin-users-list">
        {loading ?
          <div style={{ display: 'flex', flexDirection: 'column', columnGap: '10px', alignItems: 'center', justifyContent: 'center', height: '300px', color: 'gray' }}>
            <Circles height="50" width="50" color="#008751" ariaLabel="loading" /> <h3>Fetching users...</h3>
          </div> :
          users.length ? (
            users.map((user) => (
              <div className="admin-users-card" key={user._id} onClick={(e) => handleUserClick(user._id, e)}>
                <div className='admin-users-checkbox-name'>
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user._id)}
                    onChange={() => handleCheckbox(user._id)}
                  />
                  <p><strong>{user.firstname}</strong></p>
                  <p><strong>{user.lastname}</strong></p>
                </div>

                <div className="admin-users-info">
                  <p>{user.email}</p>
                  {user?.roles.map((role, index) => <p key={index}>{role}{index < user.roles.length - 1 && ','}</p>)}

                </div>
                <div className="admin-users-actions">
                  <button className="admin-users-actions-edit" onClick={() => navigate(`/admin-edit-user/${user._id}`)}><FaEdit size={15} /></button>
                  <button
                    className="admin-users-actions-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget('single');
                      setDeleteUserId(user._id);
                      setShowConfirmDialog(true);
                    }}
                  >
                    <FaTrash size={15} />
                  </button>

                </div>
              </div>
            ))
          ) : <div>No users found.</div>}
      </div>

      <div className="admin-users-pagination-controls">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          style={{ backgroundColor: currentPage === 1 ? '#ccf1e4' : '#008751' }}
        >
          Previous
        </button>
        <span className="admin-users-page-indicator">Page {currentPage} of {totalPages}</span>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          style={{ backgroundColor: currentPage === totalPages ? '#ccf1e4' : '#008751' }}
        >
          Next
        </button>
      </div>
      {showConfirmDialog && (
        <ConfirmDialog
          message={
            deleteTarget === 'single'
              ? 'Are you sure you want to delete this user?'
              : 'Are you sure you want to delete the selected users?'
          }
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowConfirmDialog(false);
            setDeleteUserId(null);
            setDeleteTarget(null);
          }}
          loading={dialogLoading}
        />
      )}

    </div>
  );
};

export default AdminUsers;